import { PdfLayout } from '../PdfLayout';
import { LayoutGrid, Mail, Gauge, Table2, FileSpreadsheet, BarChart3 } from 'lucide-react';

const benefits = [
  {
    icon: LayoutGrid,
    title: 'Drag-and-Drop Report Designer',
    text: 'Lay out headers, data tables, crosstabs, historian charts, KPI stats and images on a Letter or A4 page — plus real dashboard widgets.',
  },
  {
    icon: Mail,
    title: 'Scheduled Email Delivery',
    text: 'Every few minutes, hourly, daily, weekly, monthly or any cron expression — sent as a PDF or CSV attachment, an HTML email, or any combination.',
  },
  {
    icon: Gauge,
    title: 'OEE, Downtime & Quality Data',
    text: 'Report on OEE snapshots, downtime and quality events, and alarm history, filtered by device and date range.',
  },
  {
    icon: Table2,
    title: 'Crosstab Pivots',
    text: 'Turn events into a matrix — downtime seconds by reason and machine, rejects by reason, alarms by rule and severity.',
  },
  {
    icon: FileSpreadsheet,
    title: 'CSV for Excel',
    text: 'Download any report\'s data as CSV: each table, crosstab, chart and KPI as its own section, with raw values that open cleanly in Excel.',
  },
  {
    icon: BarChart3,
    title: 'Statistical Process Control',
    text: 'Individuals and X-bar/R control charts with Nelson rules and Cp/Cpk for critical dimensions — on screen or in a report.',
  },
];

export function Reporting() {
  return (
    <PdfLayout
      category="Analytics"
      headline="Turn Data into Decisions with"
      headlineHighlight="Reporting"
      description="Design a report once on a page grid, fill it with alarm, downtime, quality, OEE and historian data, and have it rendered to PDF, HTML or CSV and emailed on a schedule. No copying numbers by hand, no manual number crunching."
      accentColor="#16a34a"
      benefits={benefits}
    >
      <ReportMockup />
    </PdfLayout>
  );
}

function ReportMockup() {
  return (
    <div className="glass-panel p-3 w-full max-w-[280px]">
      <div className="flex items-center gap-2 mb-2 px-1">
        <div className="flex gap-1.5">
          <div className="w-2 h-2 rounded-full bg-status-fault/60" />
          <div className="w-2 h-2 rounded-full bg-status-idle/60" />
          <div className="w-2 h-2 rounded-full bg-status-running/60" />
        </div>
        <span className="text-[9px] text-text-muted">Daily Production — Cell 3</span>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-3 gap-1.5 mb-2">
        <div className="glass-card p-2 text-center">
          <div className="text-sm font-bold text-status-running">78%</div>
          <div className="text-[7px] text-text-muted">OEE</div>
        </div>
        <div className="glass-card p-2 text-center">
          <div className="text-sm font-bold text-brand-primary">1,247</div>
          <div className="text-[7px] text-text-muted">Parts Made</div>
        </div>
        <div className="glass-card p-2 text-center">
          <div className="text-sm font-bold text-status-idle">42m</div>
          <div className="text-[7px] text-text-muted">Downtime</div>
        </div>
      </div>

      {/* Bar chart */}
      <div className="glass-card p-2 mb-2">
        <span className="text-[7px] text-text-muted block mb-1.5">Output by Hour</span>
        <div className="flex items-end gap-1 h-14">
          {[65, 80, 72, 90, 85, 45, 88, 92].map((v, i) => (
            <div key={i} className="flex-1 h-full flex flex-col items-center justify-end gap-0.5">
              <div
                className="w-full rounded-t-sm"
                style={{
                  height: `${(v / 92) * 44}px`,
                  backgroundColor: v < 60 ? '#ca8a04' : '#16a34a',
                  opacity: 0.7,
                }}
              />
              <span className="text-[5px] text-text-muted">{6 + i}:00</span>
            </div>
          ))}
        </div>
      </div>

      {/* Machine breakdown */}
      <div className="glass-card p-2">
        <span className="text-[7px] text-text-muted block mb-1">Machine Breakdown</span>
        {[
          { name: 'CNC-01', parts: 412, oee: '82%', color: '#16a34a' },
          { name: 'CNC-02', parts: 389, oee: '76%', color: '#16a34a' },
          { name: 'CNC-03', parts: 446, oee: '81%', color: '#16a34a' },
        ].map((m) => (
          <div key={m.name} className="flex items-center gap-2 text-[7px] py-0.5">
            <span className="text-text-primary font-medium w-12">{m.name}</span>
            <div className="flex-1 h-1.5 bg-glass-border rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: m.oee, backgroundColor: m.color }} />
            </div>
            <span className="text-text-muted tabular-nums w-8 text-right">{m.parts}</span>
            <span className="text-text-muted tabular-nums w-6 text-right">{m.oee}</span>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mt-2 px-1">
        <span className="text-[8px] text-text-muted">Scheduled · every day 06:00</span>
        <span className="text-[8px] text-status-running font-medium">PDF + CSV emailed</span>
      </div>
    </div>
  );
}
